import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Heart, MessageCircle, Send } from "lucide-react";

const TAGS = ["all", "motivation", "healing", "goals", "wins", "creator"];

export default function Community() {
  const [posts, setPosts] = useState([]);
  const [tag, setTag] = useState("all");
  const [content, setContent] = useState("");
  const [postTag, setPostTag] = useState("motivation");
  const [openComments, setOpenComments] = useState({});
  const [comments, setComments] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});

  const load = async (t = tag) => {
    const r = await api.get("/posts", { params: t === "all" ? {} : { tag: t } });
    setPosts(r.data);
  };

  useEffect(() => { load(tag); /* eslint-disable-next-line */ }, [tag]);

  const share = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    await api.post("/posts", { content: content.trim(), tag: postTag });
    setContent("");
    load();
  };

  const like = async (p) => {
    const r = await api.post(`/posts/${p.post_id}/like`);
    setPosts(posts.map(x => x.post_id === p.post_id
      ? { ...x, liked: r.data.liked, likes: x.likes + (r.data.liked ? 1 : -1) }
      : x));
  };

  const toggleComments = async (p) => {
    setOpenComments(s => ({ ...s, [p.post_id]: !s[p.post_id] }));
    if (!comments[p.post_id]) {
      const r = await api.get(`/posts/${p.post_id}/comments`);
      setComments(c => ({ ...c, [p.post_id]: r.data }));
    }
  };

  const addComment = async (p) => {
    const draft = (commentDrafts[p.post_id] || "").trim();
    if (!draft) return;
    const r = await api.post(`/posts/${p.post_id}/comments`, { content: draft });
    setComments(c => ({ ...c, [p.post_id]: [...(c[p.post_id] || []), r.data] }));
    setCommentDrafts(d => ({ ...d, [p.post_id]: "" }));
    setPosts(posts.map(x => x.post_id === p.post_id ? { ...x, comment_count: x.comment_count + 1 } : x));
  };

  return (
    <div className="space-y-8" data-testid="community-page">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-[#8A8D98]">Kind & quiet</p>
        <h1 className="font-display text-4xl tracking-tight mt-2">Community feed</h1>
      </div>

      <form onSubmit={share} className="soft-card p-5" data-testid="post-form">
        <textarea value={content} onChange={e=>setContent(e.target.value)}
          rows={3} placeholder="Share a small win, a lesson, or a gentle nudge…"
          className="w-full bg-transparent outline-none resize-none text-lg"
          data-testid="post-content" />
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E5E3DF]">
          {TAGS.filter(t=>t!=="all").map(t => (
            <button type="button" key={t} onClick={()=>setPostTag(t)}
              className={`pill-btn text-xs uppercase tracking-widest ${postTag===t ? "bg-[#E07A5F] text-white" : "bg-[#F5F2EC] text-[#5b5f6e]"}`}
              data-testid={`post-tag-${t}`}
            >{t}</button>
          ))}
          <button type="submit" className="ml-auto pill-btn bg-[#2D3142] text-white hover:bg-[#E07A5F]" data-testid="post-submit">Share</button>
        </div>
      </form>

      <div className="flex flex-wrap items-center gap-2" data-testid="filter-tabs">
        {TAGS.map(t => (
          <button key={t} onClick={()=>setTag(t)}
            className={`pill-btn text-sm ${tag===t ? "bg-[#2D3142] text-white" : "bg-white text-[#2D3142] border border-[#E5E3DF] hover:border-[#2D3142]"}`}
            data-testid={`filter-${t}`}
          >{t}</button>
        ))}
      </div>

      <div className="space-y-4">
        {posts.length === 0 && <p className="text-[#8A8D98]">No posts yet — be the first to share.</p>}
        {posts.map((p, i) => (
          <article key={p.post_id} className={`soft-card p-5 rise-in rise-delay-${(i%4)+1}`} data-testid={`post-${p.post_id}`}>
            <div className="flex items-center gap-3">
              <img src={p.author_picture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${p.user_id}`} alt="" className="w-10 h-10 rounded-full object-cover" />
              <div>
                <p className="font-medium">{p.author_name || "Anonymous"}</p>
                <p className="text-xs text-[#8A8D98]">{new Date(p.created_at).toLocaleString()}</p>
              </div>
              <span className="ml-auto tag-chip bg-[#81B29A]/15 text-[#5a8b73]">{p.tag}</span>
            </div>
            <p className="mt-4 leading-relaxed whitespace-pre-wrap text-[#2D3142]">{p.content}</p>
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#E5E3DF]">
              <button onClick={()=>like(p)} className={`flex items-center gap-2 text-sm transition-colors ${p.liked ? "text-[#E07A5F]" : "text-[#8A8D98] hover:text-[#E07A5F]"}`} data-testid={`like-${p.post_id}`}>
                <Heart className="w-4 h-4" fill={p.liked ? "currentColor" : "none"} /> {p.likes}
              </button>
              <button onClick={()=>toggleComments(p)} className="flex items-center gap-2 text-sm text-[#8A8D98] hover:text-[#2D3142]" data-testid={`comment-toggle-${p.post_id}`}>
                <MessageCircle className="w-4 h-4" /> {p.comment_count}
              </button>
            </div>

            {openComments[p.post_id] && (
              <div className="mt-4 space-y-3" data-testid={`comments-${p.post_id}`}>
                {(comments[p.post_id] || []).map(c => (
                  <div key={c.comment_id} className="flex gap-3 items-start">
                    <img src={c.author_picture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${c.user_id}`} className="w-7 h-7 rounded-full object-cover" alt="" />
                    <div className="bg-[#F5F2EC] rounded-2xl px-4 py-2 flex-1">
                      <p className="text-sm font-medium">{c.author_name || "Anonymous"}</p>
                      <p className="text-sm text-[#2D3142]">{c.content}</p>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input value={commentDrafts[p.post_id] || ""}
                    onChange={e=>setCommentDrafts(d=>({...d, [p.post_id]: e.target.value}))}
                    placeholder="Leave a kind word…"
                    className="flex-1 bg-[#F5F2EC] rounded-full px-4 py-2 outline-none focus:ring-1 focus:ring-[#E07A5F]"
                    data-testid={`comment-input-${p.post_id}`}
                  />
                  <button onClick={()=>addComment(p)} className="pill-btn bg-[#2D3142] text-white p-3" data-testid={`comment-send-${p.post_id}`}><Send className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
